import { Avatar, Badge, Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MdClose } from "react-icons/md";
import { useSelector } from "react-redux";
import logo_text from "../../assets/logo.webp";
import useWindowDimensions from "../../utils/hooks/windowDimensions";
import { userDashboardRoutes, userFromUserSideRoutes } from "../../utils/sideRoute";
import { exit_session } from "../../config/apiHandle/apiHandle";
import CustomModal from "../common/CustomModal/CustomModal";
import { RxCross2 } from "react-icons/rx";
import ButtonComp from "../common/ButtonComp";
import { HiOutlineLogout } from "react-icons/hi";
import { imgUrl } from "../../utils/imageUrl";

export const UserSideBar = (props) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);

  const { width } = useWindowDimensions();
  const { active, hidden, activeChangeHandle } = props;

  const { sideBarHide } = useSelector((state) => state.layout_controler);
  const { user } = useSelector((state) => state.userAuth);
  const { totalUnreadCount } = useSelector((state) => state.chatManage);

  const { pathname } = useLocation();

  const storedUserRoleString = localStorage.getItem("user_role");
  let storedUserRoleNumber;

  try {
    storedUserRoleNumber = parseInt(storedUserRoleString, 10);
    if (isNaN(storedUserRoleNumber)) {
      throw new Error("Invalid number");
    }
  } catch (error) {
    console.error("Error parsing user role:", error);
  }

  const isRoleMatch = storedUserRoleNumber === 2;
  const isRoleMatchTwo = storedUserRoleNumber === 3;
  const routes = isRoleMatch ? userDashboardRoutes : isRoleMatchTwo ? userFromUserSideRoutes : [];

  return (
    <>
      <Stack
        className={`sidebar ${active && "active"} ${hidden && "hidden-side-bar"} ${sideBarHide && "hideSideBar"}`}
        sx={{
          background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)",
          justifyContent: "space-between",
          minHeight: "100vh",
        }}
      >
        <Stack>
          {/* Logo Section */}
          {width > 850 ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ py: 3, px: 2 }}
            >
              <img
                onClick={() => navigate("/")}
                src={logo_text}
                alt="logo"
                style={{
                  maxWidth: 140,
                  height: 40,
                  objectFit: "contain",
                  cursor: "pointer",
                }}
              />
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, pt: 4, pb: 2 }}
            >
              <img
                onClick={() => navigate("/")}
                src={logo_text}
                alt="logo"
                style={{
                  width: 100,
                  objectFit: "contain",
                  cursor: "pointer",
                }}
              />
              <IconButton onClick={activeChangeHandle}>
                <MdClose style={{ color: "white" }} />
              </IconButton>
            </Stack>
          )}

          {/* User Profile Section */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              mx: 2,
              mb: 3,
              p: 2,
              borderRadius: "14px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <Avatar
              src={user?.avatar ? `${imgUrl}${user.avatar}` : null}
              sx={{
                width: 44,
                height: 44,
                backgroundColor: "#F15A24",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              {user?.first_name?.[0] || "U"}
            </Avatar>
            <Stack sx={{ overflow: "hidden", flex: 1 }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "14px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user?.first_name || "User"} {user?.last_name?.[0] || ""}
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.5)",
                  fontSize: "12px",
                }}
              >
                {user?.subscription_id ? "Done Member" : isRoleMatch ? "Pro" : "Customer"}
              </Typography>
            </Stack>
          </Stack>

          {/* Menu Label */}
          <Typography
            sx={{
              color: "rgba(255,255,255,0.4)",
              fontSize: "11px",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "1px",
              px: 3,
              mb: 1,
            }}
          >
            Menu
          </Typography>

          {/* Navigation Items */}
          <Stack spacing={0.5} sx={{ px: 1 }}>
            {routes.map((e, index) => {
              const { caption, linkTo, icon, list_in_sidebar } = e;
              const isActivePath = `/dashboard${linkTo}`;
              const isActive = pathname === isActivePath;

              if (!list_in_sidebar) return null;

              return (
                <NavLink
                  key={index}
                  to={`/dashboard${linkTo}`}
                  style={{ textDecoration: "none" }}
                  onClick={() => {
                    if (width < 851) activeChangeHandle();
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      px: 2,
                      py: 1.5,
                      mx: 1,
                      borderRadius: "12px",
                      background: isActive
                        ? "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)"
                        : "transparent",
                      boxShadow: isActive ? "0 4px 15px rgba(241, 90, 36, 0.3)" : "none",
                      transition: "all 0.2s ease",
                      cursor: "pointer",
                      "&:hover": {
                        background: isActive
                          ? "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)"
                          : "rgba(255, 255, 255, 0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: "white",
                        opacity: isActive ? 1 : 0.7,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {icon}
                    </Box>
                    <Typography
                      sx={{
                        color: "white",
                        fontSize: "14px",
                        fontWeight: isActive ? 600 : 400,
                        flex: 1,
                      }}
                    >
                      {caption}
                    </Typography>
                    {caption === "Messages" && totalUnreadCount > 0 && (
                      <Badge
                        badgeContent={totalUnreadCount}
                        max={99}
                        sx={{
                          "& .MuiBadge-badge": {
                            backgroundColor: isActive ? "white" : "#F15A24",
                            color: isActive ? "#F15A24" : "white",
                            fontSize: "10px",
                            minWidth: "20px",
                            height: "20px",
                            fontWeight: 600,
                          },
                        }}
                      />
                    )}
                  </Stack>
                </NavLink>
              );
            })}
          </Stack>
        </Stack>

        {/* Bottom Section */}
        <Stack sx={{ p: 2 }}>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 2 }} />

          {/* Logout Button */}
          <Stack
            direction="row"
            alignItems="center"
            onClick={() => setOpenModal(true)}
            sx={{
              px: 2,
              py: 1.5,
              mx: 1,
              borderRadius: "12px",
              cursor: "pointer",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "rgba(241, 90, 36, 0.15)",
                borderColor: "rgba(241, 90, 36, 0.3)",
              },
            }}
            spacing={1.5}
          >
            <HiOutlineLogout style={{ color: "#fff", fontSize: "20px" }} />
            <Typography sx={{ color: "#fff", fontSize: "14px", fontWeight: 500 }}>
              Sign Out
            </Typography>
          </Stack>
        </Stack>

        {/* Logout Modal */}
        <CustomModal isOpen={openModal} setIsOpen={() => setOpenModal(!openModal)}>
          <Stack
            sx={{
              backgroundColor: "white",
              borderRadius: "16px",
              p: 3,
              maxWidth: "360px",
              margin: "auto",
            }}
            alignItems="center"
          >
            <Stack sx={{ width: "100%" }} alignItems="flex-end">
              <IconButton onClick={() => setOpenModal(!openModal)}>
                <RxCross2 size={20} />
              </IconButton>
            </Stack>

            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#fff3f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <HiOutlineLogout style={{ color: "#F15A24", fontSize: "28px" }} />
            </Box>

            <Typography sx={{ fontSize: "18px", fontWeight: 600, mb: 1, textAlign: "center" }}>
              Sign Out?
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#666", mb: 3, textAlign: "center" }}>
              Are you sure you want to sign out of your account?
            </Typography>

            <Stack direction="row" gap={2} sx={{ width: "100%" }}>
              <ButtonComp
                onClick={() => setOpenModal(!openModal)}
                label="Cancel"
                style={{
                  flex: 1,
                  height: "44px",
                  borderRadius: "12px",
                  background: "#f5f5f5",
                  color: "#333",
                }}
              />
              <ButtonComp
                onClick={() => exit_session()}
                label="Sign Out"
                style={{
                  flex: 1,
                  height: "44px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #F15A24 0%, #C53F10 100%)",
                }}
              />
            </Stack>
          </Stack>
        </CustomModal>
      </Stack>
    </>
  );
};
